<template>
	<DsHeader :cta-buttons="navModel.ctaButtons"
			  :labels="navModel.labels"
			  :logo="navModel.logo"
			  :logged-in="loggedIn"
			  :enable-basket="basketIsEnabled"
			  :basket-count="count"
			  :enable-search="navModel.enableSearch"
			  :search-fn="toggleSearch"
			  :enable-sticky="navModel.enableSticky"
			  :nav-items="navModel.navItems"
			  :service-nav-items="navModel.serviceNavItems"
			  :basket-fn="triggerFlyoutBasket"
			  :country-selector="countrySelectorSorted"
			  @service-click="handleServiceClick">
		<!-- Static markup (for SEO) -->
		<slot></slot>
		<!--Global search desktop-->
		<template #searchDesktop v-if="navModel?.enableSearch">
			<slot name="search"></slot>
		</template>
		<!--Global search mobile-->
		<template #searchMobile v-if="navModel?.enableSearch">
			<slot name="search"></slot>
		</template>
	</DsHeader>
</template>

<script setup lang="ts">
	import { ref, computed, watch } from 'vue';
	import { useServiceNavigationClick } from 'shared/composables/useServiceNavigationClick';
	import type { GlobalNavigationProps } from './GlobalNavigation.types';
	import Events from 'shared/settings/events';
	import type { CountryNavItem } from '~/@coloplast/design-system/dist/types/components/header/interface';
	import { DsHeader } from '@coloplast/design-system';
	import { useStore } from 'vuex';

	const props = withDefaults(defineProps<GlobalNavigationProps>(), {
		vm: () => ({
			baseViewModel: {} as GlobalNavigationProps['vm']['baseViewModel'],
		}),
	});

	const store = useStore();
	const hideBasketWhenEmpty = ref(props?.vm?.baseViewModel?.hideBasketWhenEmpty);
	const navModel = computed(() => props?.vm?.baseViewModel);
	const showSearchPopup = computed(() => store.getters.getShowSearchPopup);
	const isSearchPage = computed(() => store.getters.getIsSearchPage);
	const loggedIn = ref(props?.vm?.baseViewModel?.loggedIn);
	const basketIsEnabled = ref(props?.vm?.baseViewModel?.enableBasket);
	const basketItemsCount = ref(props?.vm?.baseViewModel?.basketCount);
	const numberOfBasketItems = computed(() => store.getters.getBasketItemCount);
	const count = computed(() => {
		if (numberOfBasketItems?.value == 0) {
			return basketItemsCount.value;
		} else {
			return numberOfBasketItems.value;
		}
	});
	const { handleServiceClick } = useServiceNavigationClick();

	const countrySelectorSorted = computed(() => {
		if (!navModel.value.countrySelector) {
			return undefined;
		}

		const countrySelectorModel = {
			...navModel.value.countrySelector,
			countryList: [...(navModel.value.countrySelector?.countryList || [])]
				.map((country) => ({
					...country,
					attributes: {
						lang: country.language,
						'data-country': country.name,
					},
				}))
				.sort((a, b) => a.name.localeCompare(b.name)) as CountryNavItem[],
		};

		return countrySelectorModel;
	});

	watch(
		() => isSearchPage.value,
		(newValue) => {
			if (newValue) {
				//if search page - disabled search icon in nav
				const searchButton = document?.getElementsByClassName(
					'ds-header-search',
				)?.[0] as HTMLButtonElement | undefined;
				if (searchButton) {
					searchButton.disabled = true;
				}
			}
		},
		{ immediate: true },
	);

	watch(
		() => count.value,
		(newValue) => {
			//if we need to hide basket when empty then rely on basket items count
			if (
				hideBasketWhenEmpty.value &&
				props?.vm?.baseViewModel?.enableBasket
			) {
				//if empty && hide basket when empty
				if (newValue === 0) {
					basketIsEnabled.value = false;
				} else {
					basketIsEnabled.value = true;
				}
			}
		},
		{ immediate: true },
	);

	watch(
		() => numberOfBasketItems.value,
		(newValue, oldValue) => {
			if (oldValue) {
				basketItemsCount.value = newValue;
			}
		},
		{ immediate: true },
	);

	function triggerFlyoutBasket() {
		const openFlyoutBasket = new CustomEvent(
			Events.ACTIVATE_FLY_OUT_BASKET,
			{},
		);
		document.dispatchEvent(openFlyoutBasket);
	}

	function toggleSearch() {
		store.commit('setShowSearchPopup', !showSearchPopup.value);
	}
</script>

<style lang="scss">
	@import 'multisite/styles/settings/settings';
	@import 'multisite/styles/tools/tools';
	@import 'multisite/styles/components/c-menu-centered__settings';
	@import 'multisite/styles/components/c-nav-service';

	.ds-nav-mega-search,
	.ds-header-mobile__search {
		.ds-nav-mega-search__content {
			padding-block-start: 0;
		}

		.c-global-search__popup {
			all: unset;

			&-stickybox {
				padding: 0;

				&::after {
					all: unset;
				}
			}
		}

		.c-global-search-autosuggestions__box {
			margin-left: 0;
		}
	}

	.c-nav-service-design {
		z-index: 110;
	}

	/*for razor view (default slot)*/
	@media (min-width: ds-breakpoint('md')) {
		.ds-header-visible-mobile {
			display: none !important;
		}
	}

	@media (max-width: ds-breakpoint('md')) {
		.ds-header-visible-desktop {
			display: none !important;
		}
	}
</style>
